import { useState, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import { useMutation, useQuery } from "@apollo/client";
import {
  ENABLE_WATCH_TOWER,
  DISABLE_WATCH_TOWER,
  WATCH_TOWER_STATUS,
} from "../../../../apis/settingsApis/assets-apis/assetsApis";

/**
 * Rego WatchTower is an agent-based MDM source — there is no OAuth / Truto
 * connect. "Connecting" enables it for the tenant; employees then self-enroll
 * their devices from their Assets page. This drawer shows that intent + a single
 * confirm action.
 */
const WatchTowerConnectionDrawer = ({
  open,
  onClose,
  integrationLabel = "Rego WatchTower",
  refetchIntegrationDetails,
}) => {
  const theme = useTheme();
  const [error, setError] = useState("");

  const { data: statusData } = useQuery(WATCH_TOWER_STATUS, {
    fetchPolicy: "cache-and-network",
    skip: !open,
  });
  const isConnected = statusData?.watchTowerStatus?.enabled === true;

  const [enableWatchTower, { loading: enabling }] = useMutation(ENABLE_WATCH_TOWER);
  const [disableWatchTower, { loading: disabling }] = useMutation(DISABLE_WATCH_TOWER);

  const slideTransition = useMemo(
    () => (props) => <Slide {...props} direction="left" />,
    []
  );

  const handleEnable = async () => {
    setError("");
    try {
      const { data } = await enableWatchTower();
      const payload = data?.enableWatchTower;
      if (payload?.success) {
        if (refetchIntegrationDetails) await refetchIntegrationDetails();
        onClose();
      } else {
        setError(payload?.message || "Failed to enable Rego WatchTower.");
      }
    } catch (e) {
      setError(e.message || "An error occurred while enabling Rego WatchTower.");
    }
  };

  const handleDisable = async () => {
    setError("");
    try {
      const { data } = await disableWatchTower();
      const payload = data?.disableWatchTower;
      if (payload?.success) {
        if (refetchIntegrationDetails) await refetchIntegrationDetails();
        onClose();
      } else {
        setError(payload?.message || "Failed to disconnect Rego WatchTower.");
      }
    } catch (e) {
      setError(e.message || "An error occurred while disconnecting Rego WatchTower.");
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      TransitionComponent={slideTransition}
      PaperProps={{
        sx: {
          width: "60%",
          maxWidth: "900px",
          backgroundColor: theme.customColors.background.main,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#FFFFFF",
          borderBottom: `1px solid ${theme.customColors.border.subtle}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 4,
            py: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "10px",
                backgroundColor: "#e3f2fd",
                color: "#1976d2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DevicesOtherIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.customColors.text.strong }}>
                Connect {integrationLabel}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.customColors.text.secondary }}>
                Agent-based device monitoring
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Body (scrolls) */}
      <Box sx={{ p: 4, flex: 1, overflowY: "auto" }}>
        <Alert severity={isConnected ? "success" : "info"} sx={{ mb: 3 }}>
          {isConnected ? (
            <>
              <strong>{integrationLabel}</strong> is connected. Employees can enroll
              their devices from the Assets page. Disconnecting hides the enroll
              flow and stops accepting new device syncs — existing device records
              are kept.
            </>
          ) : (
            <>
              Enabling <strong>{integrationLabel}</strong> turns on agent-based device
              monitoring for your organization. Unlike other integrations, there's
              nothing to authorize. Your employees enroll their own devices.
            </>
          )}
        </Alert>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: theme.customColors.text.strong }}>
          What happens after you connect
        </Typography>
        <Box component="ol" sx={{ pl: 2.5, m: 0, mb: 3, color: theme.customColors.text.secondary, "& li": { mb: 1 } }}>
          <li>Rego WatchTower appears as an active MDM source on your Assets page.</li>
          <li>Employees go to <strong>Assets → Connect Device</strong> to generate an enrollment token.</li>
          <li>They install the WatchTower agent and paste the token to enroll their machine.</li>
          <li>Enrolled devices report their security posture back automatically.</li>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Footer (pinned to bottom, bordered) */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          gap: 1.5,
          justifyContent: "flex-end",
          alignItems: "center",
          px: 4,
          py: 2.5,
          backgroundColor: "#FFFFFF",
          borderTop: `1px solid ${theme.customColors.border.subtle}`,
        }}
      >
        <Button onClick={onClose} sx={{ color: theme.customColors.text.secondary, textTransform: "none" }}>
          Cancel
        </Button>
        {isConnected ? (
          <Button
            variant="outlined"
            color="error"
            onClick={handleDisable}
            disabled={disabling}
            sx={{ textTransform: "none", fontWeight: 600, px: 3 }}
          >
            {disabling ? <CircularProgress size={18} color="error" /> : "Disconnect"}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleEnable}
            disabled={enabling}
            sx={{
              backgroundColor: "#031B89",
              "&:hover": { backgroundColor: "#2137a6" },
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {enabling ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Connect Rego WatchTower"}
          </Button>
        )}
      </Box>
    </Drawer>
  );
};

export default WatchTowerConnectionDrawer;
